// /agents/orchestration used to be the Groups graph. That page is gone;
// the URL redirects to Agents. Group/thread management stays on each
// orchestration agent's detail page. GroupCard is still exported from here.
import { useClient } from '@cortex/react-query';
import type {
  PersistentAgent,
  PersistentAgentGroup,
  PersistentAgentListItem,
  PersistentAgentPeerCapabilities,
  PersistentAgentPeerGrantInput,
  Spec,
} from '@cortex/types';
import { fork } from '@cortex/utils/fork';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Fragment, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { Modal } from '../../../components/common/Modal';
import { Spinner } from '../../../components/common/Spinner';
import {
  PeerGrantsEditor,
  fromPeerGrantDraft,
  toPeerGrantDraft,
  type IPeerGrantDraft,
} from '../../../components/persistent-agents/PeerGrantsEditor';
import { ProgressBar } from '../../../components/persistent-agents/ProgressBar';
import { routes } from '../../../lib/constants';
import { formatAge, formatDateTime, formatRelative } from '../../../lib/format';
import { resolveStatusColor } from '../../../lib/status-color';

export const Route = createFileRoute('/agents/orchestration/')({
  beforeLoad: () => {
    throw redirect({ to: routes.AGENTS, replace: true });
  },
});

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

/** List-view shape — Spec plus the fields the list endpoint adds (mirrors the Mission Specs page). */
type SpecListItem = Spec & { agentIds: string[]; sectionCount: number };

/** Status → badge variant, so the table reads at a glance. */
const statusBadge: Record<string, string> = {
  STARTING: 'badge badge--warning',
  IDLE: 'badge badge--neutral',
  WORKING: 'badge badge--success',
  PAUSED: 'badge badge--warning',
  WAITING_FOR_INPUT: 'badge badge--warning',
  STOPPED: 'badge badge--default',
  FAILED: 'badge badge--danger',
};

/**
 * Compact heartbeat readout for a thread row. The list polls every 5s, so
 * "next fire" stays fresh without any extra wiring. Dash when the thread has
 * no heartbeats; a single heartbeat shows its rate + next fire inline (today's
 * at-a-glance UX); several show a count plus the soonest upcoming fire among
 * the active ones.
 */
function HeartbeatCell({ heartbeats }: { heartbeats: PersistentAgentListItem['heartbeats'] }) {
  if (heartbeats.length === 0) return <span className="t-muted">—</span>;
  const heartbeat = heartbeats.length === 1 ? heartbeats[0] : undefined;
  if (heartbeat) {
    if (heartbeat.status === 'PAUSED') {
      return (
        <span title={`Heartbeat ${heartbeat.cron} (${heartbeat.timezone ?? 'UTC'}) — paused`}>
          ♥ <code>{heartbeat.cron}</code> <span className="badge badge--warning">paused</span>
        </span>
      );
    }
    return (
      <span
        title={`Heartbeat ${heartbeat.cron} (${heartbeat.timezone ?? 'UTC'}) — next fire ${formatDateTime(heartbeat.nextFireAt)}`}
      >
        ♥ <code>{heartbeat.cron}</code>{' '}
        <span className="t-muted">· {formatRelative(heartbeat.nextFireAt)}</span>
      </span>
    );
  }
  const active = heartbeats.filter((h) => h.status === 'ACTIVE');
  if (active.length === 0) {
    return (
      <span title={`${heartbeats.length} heartbeats — all paused`}>
        ♥ {heartbeats.length} heartbeats <span className="badge badge--warning">paused</span>
      </span>
    );
  }
  const soonest = active.reduce((a, b) => (a.nextFireAt < b.nextFireAt ? a : b));
  return (
    <span
      title={`${heartbeats.length} heartbeats — next fire ${formatDateTime(soonest.nextFireAt)}`}
    >
      ♥ {heartbeats.length} heartbeats{' '}
      <span className="t-muted">· next {formatRelative(soonest.nextFireAt)}</span>
    </span>
  );
}

type InitialFactRow = { laneId: string; summary: string; required?: boolean };

/** One-line human rendering of a grant's capabilities (for read-only lists). */
function summarizeCapabilities(caps: PersistentAgentPeerCapabilities): string {
  const parts: string[] = [];
  if (caps.listThreads) parts.push('list threads');
  if (caps.readLogs) parts.push('read logs');
  if (caps.startThread) parts.push('start threads');
  if (caps.stopThread) parts.push('STOP threads');
  if (caps.sendEvent) {
    parts.push(`send ${caps.sendEvent.kinds.join('/')} (${caps.sendEvent.deliveries.join(', ')})`);
  }
  if (caps.broadcastEvent) {
    parts.push(
      `broadcast ${caps.broadcastEvent.kinds.join('/')} (${caps.broadcastEvent.deliveries.join(', ')})`,
    );
  }
  if (caps.putFact) parts.push(`set signals: ${caps.putFact.lanes.join(', ')}`);
  if (caps.delegateTask) parts.push('delegate tasks');
  if (caps.manageWorkers) parts.push(`manage up to ${caps.manageWorkers.maxWorkers} workers`);
  return parts.join(' · ') || 'nothing';
}

export function GroupCard({
  def,
  title,
  managementActionsTargetId,
}: {
  def: PersistentAgentGroup;
  title?: string;
  /** Optional page-header portal used by the standalone agent detail page. */
  managementActionsTargetId?: string;
}) {
  const client = useClient();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [initialFacts, setInitialFacts] = useState<InitialFactRow[]>([]);
  const [threadName, setThreadName] = useState('');
  const [showStart, setShowStart] = useState(false);
  const [showDeleteStopped, setShowDeleteStopped] = useState(false);
  const [showPeers, setShowPeers] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const [showFilesystem, setShowFilesystem] = useState(false);
  // '' = detach; seeded from the group row when the modal opens.
  const [filesystemChoice, setFilesystemChoice] = useState('');
  const [showSpecs, setShowSpecs] = useState(false);
  // null = "not touched yet" → derived from the specs list once it's loaded.
  const [specDraft, setSpecDraft] = useState<string[] | null>(null);
  // null = "not touched yet" → seeded from the detail query when it lands.
  const [peerDrafts, setPeerDrafts] = useState<IPeerGrantDraft[] | null>(null);

  // All groups (cache-shared with the page query) — the target choices for grants.
  const groupsQuery = useQuery({
    queryKey: ['persistent-agents'],
    queryFn: () =>
      client.http.get('v1/persistent-agent-groups').json<IListEnvelope<PersistentAgentGroup>>(),
    enabled: showPeers,
  });

  // Group detail (peers + grantedBy) — only fetched while the modal is open.
  const detailQuery = useQuery({
    queryKey: ['persistent-agents', def.id, 'detail'],
    queryFn: () =>
      client.http.get(`v1/persistent-agent-groups/${def.id}`).json<PersistentAgentGroup>(),
    enabled: showPeers,
  });

  // Filesystems to offer in the attach modal — only fetched while it's open.
  const filesystemsQuery = useQuery({
    queryKey: ['persistent-agent-filesystems'],
    queryFn: () =>
      client.http
        .get('v1/persistent-agent-filesystems')
        .json<IListEnvelope<{ id: string; name: string }>>(),
    enabled: showFilesystem,
  });

  const saveFilesystem = useMutation({
    mutationFn: (filesystemId: string | null) =>
      client.http
        .patch(`v1/persistent-agent-groups/${def.id}`, { json: { filesystemId } })
        .json<PersistentAgentGroup>(),
    onSuccess: async () => {
      setShowFilesystem(false);
      await qc.invalidateQueries({ queryKey: ['persistent-agents'] });
      fork(qc.invalidateQueries({ queryKey: ['persistent-agent-filesystems'] }));
    },
  });

  // Mission specs to offer in the attach modal — only fetched while it's open.
  // Shares its query key with the Mission Specs page, so the cache (and any
  // count badges there) stay consistent with what gets bound here.
  const specsQuery = useQuery({
    queryKey: ['specs'],
    queryFn: () => client.http.get('v1/specs').json<IListEnvelope<SpecListItem>>(),
    enabled: showSpecs,
  });
  const allSpecs = specsQuery.data?.items ?? [];
  // Binding is stored on the spec (agentIds), not on the group — derive the
  // currently-bound set by scanning specs for this agent's id.
  const boundSpecIds =
    specDraft ?? allSpecs.filter((s) => s.agentIds.includes(def.id)).map((s) => s.id);

  function toggleSpec(specId: string) {
    setSpecDraft(
      boundSpecIds.includes(specId)
        ? boundSpecIds.filter((id) => id !== specId)
        : [...boundSpecIds, specId],
    );
  }

  const saveSpecs = useMutation({
    mutationFn: async (nextSpecIds: string[]) => {
      // Only rebind specs whose membership actually changed — each spec's
      // binding list is independent, so this is a set of per-spec PATCHes.
      const changed = allSpecs.filter(
        (s) => s.agentIds.includes(def.id) !== nextSpecIds.includes(s.id),
      );
      await Promise.all(
        changed.map((s) => {
          const agentIds = s.agentIds.includes(def.id)
            ? s.agentIds.filter((id) => id !== def.id)
            : [...s.agentIds, def.id];
          return client.http.patch(`v1/specs/${s.id}/bindings`, { json: { agentIds } }).json();
        }),
      );
    },
    onSuccess: async () => {
      setShowSpecs(false);
      setSpecDraft(null);
      await qc.invalidateQueries({ queryKey: ['specs'] });
    },
  });

  const savePeers = useMutation({
    mutationFn: (peers: PersistentAgentPeerGrantInput[]) =>
      client.http
        .patch(`v1/persistent-agent-groups/${def.id}`, { json: { peers } })
        .json<PersistentAgentGroup>(),
    onSuccess: () => {
      setShowPeers(false);
      setPeerDrafts(null);
      fork(qc.invalidateQueries({ queryKey: ['persistent-agents'] }));
    },
  });

  const sessionsQuery = useQuery({
    queryKey: ['persistent-agents', def.id, 'agents'],
    queryFn: () =>
      client.http
        .get(`v1/persistent-agent-groups/${def.id}/agents`)
        .json<IListEnvelope<PersistentAgentListItem>>(),
    refetchInterval: 5000,
  });

  const start = useMutation({
    mutationFn: () => {
      const facts = initialFacts
        .filter((f) => f.laneId.trim() && f.summary.trim())
        .map((f) => ({
          laneId: f.laneId.trim(),
          summary: f.summary.trim(),
          data: { text: f.summary.trim() },
        }));
      return client.http
        .post(`v1/persistent-agent-groups/${def.id}/agents`, {
          json: {
            initialFacts: facts,
            ...(threadName.trim() ? { name: threadName.trim() } : {}),
          },
        })
        .json<PersistentAgent>();
    },
    onSuccess: () => {
      setInitialFacts([]);
      setThreadName('');
      setShowStart(false);
      fork(qc.invalidateQueries({ queryKey: ['persistent-agents', def.id, 'agents'] }));
    },
  });

  const removeStopped = useMutation({
    mutationFn: () =>
      client.http
        .delete(`v1/persistent-agent-groups/${def.id}/agents/stopped`)
        .json<{ deleted: number }>(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['persistent-agents', def.id, 'agents'] });
      setShowDeleteStopped(false);
    },
  });

  // Toggles whether a thread is surfaced on the chat + MCP listing surfaces
  // (`orchestration_list_threads`); hidden by default. Console listing itself
  // is unaffected — this only flips the flag other surfaces read.
  const setVisibleToChat = useMutation({
    mutationFn: ({ threadId, visibleToChat }: { threadId: string; visibleToChat: boolean }) =>
      client.http
        .patch(`v1/persistent-agent-groups/${def.id}/agents/${threadId}`, {
          json: { visibleToChat },
        })
        .json<PersistentAgent>(),
    onSuccess: () =>
      fork(qc.invalidateQueries({ queryKey: ['persistent-agents', def.id, 'agents'] })),
  });

  const stopThread = useMutation({
    mutationFn: (threadId: string) =>
      client.http
        .post(`v1/persistent-agent-groups/${def.id}/agents/${threadId}/stop`)
        .json<PersistentAgent>(),
    onSuccess: () =>
      fork(qc.invalidateQueries({ queryKey: ['persistent-agents', def.id, 'agents'] })),
  });
  const deleteThread = useMutation({
    mutationFn: (threadId: string) =>
      client.http.delete(`v1/persistent-agent-groups/${def.id}/agents/${threadId}`),
    onSuccess: () =>
      fork(qc.invalidateQueries({ queryKey: ['persistent-agents', def.id, 'agents'] })),
  });

  const stoppedCount = (sessionsQuery.data?.items ?? []).filter(
    (s) => s.status === 'STOPPED' || s.status === 'FAILED',
  ).length;

  const sessions = sessionsQuery.data?.items ?? [];

  // The "…" menu is rendered through a portal to <body> so it escapes this
  // card's `overflow: hidden` (which otherwise clips it once it grows past a
  // couple of items) — mirrors InfoHint's popover pattern. Position is
  // computed from the trigger's bounding rect, right-aligned to it.
  const repositionMenu = useCallback(() => {
    const el = menuTriggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
  }, []);

  useLayoutEffect(() => {
    if (!showMenu) return;
    repositionMenu();
    const onScroll = () => repositionMenu();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [showMenu, repositionMenu]);

  // The launch dialog starts pre-seeded with the group's required lanes (locked
  // key, empty value) so the launcher just fills in values.
  function openLaunch() {
    setInitialFacts(
      def.requiredFacts.map((rf) => ({ laneId: rf.laneId, summary: '', required: true })),
    );
    setThreadName('');
    setShowStart(true);
  }

  const requiredFilled = initialFacts
    .filter((f) => f.required)
    .every((f) => f.summary.trim().length > 0);

  // Grants under edit: local drafts once touched, else the loaded detail.
  const drafts = peerDrafts ?? (detailQuery.data?.peers ?? []).map(toPeerGrantDraft);
  // A row with a target but no enabled capability is incomplete — block save.
  const incompleteDraft = drafts.some((d) => d.targetAgentId && !fromPeerGrantDraft(d));
  const grantedBy = detailQuery.data?.grantedBy ?? [];

  const managementActionsTarget =
    managementActionsTargetId && typeof document !== 'undefined'
      ? document.getElementById(managementActionsTargetId)
      : null;

  return (
    <div className="card">
      {managementActionsTarget &&
        createPortal(
          <>
            <button
              className="btn btn--secondary btn--sm"
              onClick={() => {
                setPeerDrafts(null);
                savePeers.reset();
                setShowPeers(true);
              }}
            >
              Permissions
            </button>
            <button
              className="btn btn--secondary btn--sm"
              onClick={() => {
                setFilesystemChoice(def.filesystemId ?? '');
                saveFilesystem.reset();
                setShowFilesystem(true);
              }}
            >
              Filesystem
            </button>
            <button
              className="btn btn--secondary btn--sm"
              onClick={() => {
                setSpecDraft(null);
                saveSpecs.reset();
                setShowSpecs(true);
              }}
            >
              Mission Specs
            </button>
          </>,
          managementActionsTarget,
        )}
      <div
        className="card-header"
        style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 'var(--fs-lg)' }}>{title ?? def.name}</span>
        {def.disabledAt && <span className="badge badge--danger">disabled</span>}
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', flexShrink: 0 }}>
          <button
            className="btn btn--secondary btn--sm"
            disabled={!!def.disabledAt}
            onClick={openLaunch}
          >
            Start thread
          </button>
          {(!managementActionsTargetId || stoppedCount > 0) && (
            <div style={{ position: 'relative' }}>
              <button
                ref={menuTriggerRef}
                className="btn btn--secondary btn--sm"
                aria-label="More actions"
                aria-expanded={showMenu}
                title="Manage permissions, filesystem, mission specs, and stopped threads"
                onClick={() => setShowMenu((v) => !v)}
              >
                ⋯
              </button>
              {showMenu &&
                typeof document !== 'undefined' &&
                createPortal(
                  <>
                    {/* Invisible backdrop: any outside click closes the menu. */}
                    <div
                      style={{ position: 'fixed', inset: 0, zIndex: 20 }}
                      onClick={() => setShowMenu(false)}
                    />
                    <div
                      className="card"
                      style={{
                        position: 'fixed',
                        top: menuPos.top,
                        right: menuPos.right,
                        zIndex: 21,
                        minWidth: 210,
                        padding: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      {!managementActionsTargetId && (
                        <button
                          className="btn btn--ghost btn--sm"
                          style={{ justifyContent: 'flex-start' }}
                          title="Which other persistent agents this one's threads may talk to, and how"
                          onClick={() => {
                            setShowMenu(false);
                            setPeerDrafts(null);
                            savePeers.reset();
                            setShowPeers(true);
                          }}
                        >
                          Permissions
                        </button>
                      )}
                      {!managementActionsTargetId && (
                        <button
                          className="btn btn--ghost btn--sm"
                          style={{ justifyContent: 'flex-start' }}
                          title="Attach a filesystem — threads gain file tools against it on their next build (after a reset)"
                          onClick={() => {
                            setShowMenu(false);
                            setFilesystemChoice(def.filesystemId ?? '');
                            saveFilesystem.reset();
                            setShowFilesystem(true);
                          }}
                        >
                          Filesystem{def.filesystemId ? ' (attached)' : ''}
                        </button>
                      )}
                      {!managementActionsTargetId && (
                        <button
                          className="btn btn--ghost btn--sm"
                          style={{ justifyContent: 'flex-start' }}
                          title="Attach mission specs — threads gain spec_* tools for each attached spec on their next build (after a reset)"
                          onClick={() => {
                            setShowMenu(false);
                            setSpecDraft(null);
                            saveSpecs.reset();
                            setShowSpecs(true);
                          }}
                        >
                          Mission Specs
                        </button>
                      )}
                      {/* Only offered when there is something to clear out. */}
                      {stoppedCount > 0 && (
                        <button
                          className="btn btn--ghost btn--sm"
                          style={{ justifyContent: 'flex-start' }}
                          title={`Remove ${stoppedCount} stopped/failed thread${stoppedCount === 1 ? '' : 's'}`}
                          onClick={() => {
                            setShowMenu(false);
                            setShowDeleteStopped(true);
                          }}
                        >
                          Delete stopped threads ({stoppedCount})
                        </button>
                      )}
                    </div>
                  </>,
                  document.body,
                )}
            </div>
          )}
        </div>
      </div>
      <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {sessions.length > 0 ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {/* width:1 + nowrap makes the column hug the badge. */}
                  <th style={{ width: 1, whiteSpace: 'nowrap' }}>Status</th>
                  <th>Thread</th>
                  {def.requiredFacts.map((rf) => (
                    <th key={rf.laneId}>{rf.laneId}</th>
                  ))}
                  <th>Heartbeats</th>
                  <th style={{ whiteSpace: 'nowrap' }}>Started At</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const open = () => navigate({ to: routes.orchestrationThread(def.id, s.id) });
                  const columnCount = 5 + def.requiredFacts.length;
                  // The thread's self-reported health tint (its set_status_color
                  // tool) — a rail down the row's leading edge plus a swatch, so
                  // it registers without adding a row or shifting any geometry.
                  const tint = resolveStatusColor(s.statusColor);
                  return (
                    <Fragment key={s.id}>
                      <tr onClick={open} style={{ cursor: 'pointer' }} title="Open thread">
                        <td
                          style={{
                            width: 1,
                            whiteSpace: 'nowrap',
                            ...(tint ? { boxShadow: `inset 3px 0 0 ${tint.solid}` } : {}),
                          }}
                        >
                          {tint && (
                            // role=img + aria-label so the tint is announced,
                            // not color-alone — the reason rides along in both
                            // the accessible name and the hover title.
                            <span
                              role="img"
                              aria-label={`Self-reported ${tint.label}${s.statusColorReason ? ` — ${s.statusColorReason}` : ''}`}
                              title={`${tint.label}${s.statusColorReason ? ` — ${s.statusColorReason}` : ''} · self-reported ${formatRelative(s.statusColorAt)}`}
                              style={{
                                display: 'inline-block',
                                width: 8,
                                height: 8,
                                borderRadius: 999,
                                background: tint.solid,
                                marginRight: 6,
                                verticalAlign: 'middle',
                              }}
                            />
                          )}
                          <span className={statusBadge[s.status] ?? 'badge badge--neutral'}>
                            {s.status}
                          </span>
                        </td>
                        <td>
                          {s.name ? (
                            <>
                              <span className="t-sm">{s.name}</span>{' '}
                              <span className="t-mono t-xs t-muted">{s.id.slice(0, 8)}</span>
                            </>
                          ) : (
                            <span className="t-mono t-xs">{s.id.slice(0, 8)}</span>
                          )}
                        </td>
                        {def.requiredFacts.map((rf) => (
                          <td
                            key={rf.laneId}
                            className="t-sm"
                            style={{
                              maxWidth: 240,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={s.facts[rf.laneId] ?? ''}
                          >
                            {s.facts[rf.laneId] ?? <span className="t-muted">—</span>}
                          </td>
                        ))}
                        <td className="t-xs">
                          <HeartbeatCell heartbeats={s.heartbeats} />
                        </td>
                        {/* When the CURRENT session started — `startedAt` is
                            rebased by a reset — plus how long it has been
                            running, so a glance answers both "when" and
                            "how long". */}
                        <td className="t-xs t-muted" style={{ whiteSpace: 'nowrap' }}>
                          {formatDateTime(s.startedAt)}
                          <span className="t-muted"> · {formatAge(s.startedAt)}</span>
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button
                            className="btn btn--ghost btn--sm"
                            aria-label={
                              s.visibleToChat
                                ? 'Visible to chat — click to hide'
                                : 'Hidden from chat — click to make visible'
                            }
                            title={
                              s.visibleToChat
                                ? 'Visible on the chat thread listing — click to hide'
                                : 'Hidden from the chat thread listing — click to make visible'
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              setVisibleToChat.mutate({
                                threadId: s.id,
                                visibleToChat: !s.visibleToChat,
                              });
                            }}
                            style={{
                              fontSize: 18,
                              lineHeight: 1,
                              opacity: s.visibleToChat ? 1 : 0.35,
                            }}
                          >
                            💬
                          </button>
                          {s.status !== 'STOPPED' && s.status !== 'FAILED' && (
                            <button
                              className="btn btn--secondary btn--sm"
                              disabled={stopThread.isPending}
                              title="Stop this thread permanently"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm('Stop this thread permanently?'))
                                  stopThread.mutate(s.id);
                              }}
                            >
                              Stop
                            </button>
                          )}
                          <button
                            className="btn btn--destructive btn--sm"
                            disabled={deleteThread.isPending}
                            title="Delete this thread and its history"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (
                                window.confirm(
                                  'Delete this thread and its history? This cannot be undone.',
                                )
                              )
                                deleteThread.mutate(s.id);
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                      {/* The self-reported activity line (set_status tool) gets its own
                          full-width row so a descriptive status isn't crushed into a
                          truncated cell. Visually attached to the row above. */}
                      {s.statusText && (
                        <tr
                          onClick={open}
                          style={{ cursor: 'pointer' }}
                          title={`${s.statusText} — self-reported ${formatRelative(s.statusTextAt)}`}
                        >
                          <td
                            colSpan={columnCount}
                            className="t-sm"
                            style={{
                              // The ui-kit default is 12px top+bottom; keep it symmetric
                              // (slightly compact) so the line sits vertically centered.
                              paddingTop: 8,
                              paddingBottom: 8,
                              borderTop: 'none',
                              color: 'var(--text-secondary)',
                              fontStyle: 'italic',
                            }}
                          >
                            <span
                              style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              ⚡ {s.statusText}{' '}
                              <span className="t-xs t-muted" style={{ fontStyle: 'normal' }}>
                                · {formatRelative(s.statusTextAt)}
                              </span>
                            </span>
                          </td>
                        </tr>
                      )}
                      {/* Self-reported progress bar (set_progress tool) — a full-width
                          row, stacked under the statusText row when both are set. */}
                      {s.progressBar && (
                        <tr onClick={open} style={{ cursor: 'pointer' }}>
                          <td
                            colSpan={columnCount}
                            style={{ paddingTop: 8, paddingBottom: 8, borderTop: 'none' }}
                          >
                            <ProgressBar bar={s.progressBar} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="t-sm t-muted" style={{ margin: 0 }}>
            No threads yet.
          </p>
        )}
      </div>

      <Modal
        open={showStart}
        onClose={() => setShowStart(false)}
        eyebrow={def.name}
        title="Start thread"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <button className="btn btn--ghost btn--sm" onClick={() => setShowStart(false)}>
              Cancel
            </button>
            <button
              className="btn btn--primary btn--sm"
              disabled={start.isPending || !!def.disabledAt || !requiredFilled}
              onClick={() => start.mutate()}
            >
              {start.isPending ? 'Starting…' : 'Start thread'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <label className="field">
            <span className="field-label">Name (optional)</span>
            <input
              className="input"
              placeholder="e.g. East field — night shift"
              value={threadName}
              onChange={(e) => setThreadName(e.target.value)}
              maxLength={200}
            />
          </label>
          <p className="t-sm t-muted" style={{ margin: 0 }}>
            State signals are pinned context the thread always sees — its purpose, the robot it
            watches, etc. Required lanes must be filled in.
          </p>
          {initialFacts.map((f, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {f.required ? (
                <span
                  className="t-sm t-mono"
                  style={{ flex: '0 0 180px' }}
                  title="Required by this agent"
                >
                  {f.laneId} <span style={{ color: 'var(--color-danger)' }}>*</span>
                </span>
              ) : (
                <input
                  className="input"
                  placeholder="lane (e.g. notes)"
                  value={f.laneId}
                  onChange={(e) =>
                    setInitialFacts((rows) =>
                      rows.map((r, j) => (j === i ? { ...r, laneId: e.target.value } : r)),
                    )
                  }
                  style={{ flex: '0 0 180px' }}
                />
              )}
              <input
                className="input"
                placeholder="value the thread sees"
                value={f.summary}
                onChange={(e) =>
                  setInitialFacts((rows) =>
                    rows.map((r, j) => (j === i ? { ...r, summary: e.target.value } : r)),
                  )
                }
                style={{ flex: 1 }}
              />
              {f.required ? (
                <span style={{ width: 28 }} aria-hidden="true" />
              ) : (
                <button
                  className="btn btn--ghost btn--sm"
                  aria-label="remove fact"
                  onClick={() => setInitialFacts((rows) => rows.filter((_, j) => j !== i))}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          <div>
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => setInitialFacts((rows) => [...rows, { laneId: '', summary: '' }])}
            >
              + Add state signal
            </button>
          </div>
          {start.isError && (
            <div className="alert alert--danger">
              {String((start.error as Error)?.message ?? 'Failed to start thread')}
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={showPeers}
        onClose={() => {
          if (!savePeers.isPending) {
            setShowPeers(false);
            setPeerDrafts(null);
          }
        }}
        eyebrow={def.name}
        title="Peer permissions"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <button
              className="btn btn--ghost btn--sm"
              disabled={savePeers.isPending}
              onClick={() => {
                setShowPeers(false);
                setPeerDrafts(null);
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn--primary btn--sm"
              disabled={savePeers.isPending || detailQuery.isLoading || incompleteDraft}
              onClick={() =>
                savePeers.mutate(
                  drafts
                    .map(fromPeerGrantDraft)
                    .filter((g): g is PersistentAgentPeerGrantInput => g !== null),
                )
              }
            >
              {savePeers.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <p className="t-sm t-muted" style={{ margin: 0 }}>
            What this agent&apos;s threads may do to other persistent agents. Grants are directional
            — for the peer to reply, add the mirror grant on the peer. Threads pick up new
            permissions on their next build (after a reset); revoked permissions stop working
            immediately.
          </p>
          {detailQuery.isLoading ? (
            <Spinner />
          ) : (
            <PeerGrantsEditor
              value={drafts}
              onChange={setPeerDrafts}
              groups={(groupsQuery.data?.items ?? [])
                .filter((g) => g.orchestrationGroupId === def.orchestrationGroupId)
                .map((g) => ({
                  id: g.id,
                  name: g.id === def.id ? `${g.name} (itself)` : g.name,
                }))}
            />
          )}
          {incompleteDraft && (
            <p className="t-xs" style={{ margin: 0, color: 'var(--color-danger)' }}>
              Every grant needs at least one capability enabled (and lanes/kinds filled in).
            </p>
          )}
          {grantedBy.length > 0 && (
            <div className="field">
              <span className="field-label">Granted by others</span>
              <p className="t-xs t-muted" style={{ margin: '0 0 4px' }}>
                Agents that may act on THIS one&apos;s threads — managed from their side.
              </p>
              {grantedBy.map((g) => (
                <p key={g.id} className="t-sm" style={{ margin: 0 }}>
                  <strong>{g.targetAgentName ?? g.agentId}</strong>{' '}
                  <span className="t-muted">may {summarizeCapabilities(g.capabilities)}</span>
                </p>
              ))}
            </div>
          )}
          {savePeers.isError && (
            <div className="alert alert--danger">
              {String((savePeers.error as Error)?.message ?? 'Failed to save permissions')}
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={showFilesystem}
        onClose={() => {
          if (!saveFilesystem.isPending) setShowFilesystem(false);
        }}
        eyebrow={def.name}
        title="Filesystem"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <button
              className="btn btn--ghost btn--sm"
              disabled={saveFilesystem.isPending}
              onClick={() => setShowFilesystem(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn--primary btn--sm"
              disabled={saveFilesystem.isPending}
              onClick={() =>
                saveFilesystem.mutate(filesystemChoice === '' ? null : filesystemChoice)
              }
            >
              {saveFilesystem.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <p className="t-sm t-muted" style={{ margin: 0 }}>
            Threads of this agent get file tools (read, write, edit, list, grep) against the
            attached filesystem. Applied on each thread’s next build (after a reset). Filesystems
            can be shared by several agents.
          </p>
          <label className="t-sm" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            Attached filesystem
            <select
              className="input"
              value={filesystemChoice}
              onChange={(e) => setFilesystemChoice(e.target.value)}
            >
              <option value="">— none —</option>
              {(filesystemsQuery.data?.items ?? []).map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </label>
          {saveFilesystem.isError && (
            <div className="alert alert--danger">
              {String((saveFilesystem.error as Error)?.message ?? 'Failed to save')}
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={showSpecs}
        onClose={() => {
          if (!saveSpecs.isPending) {
            setShowSpecs(false);
            setSpecDraft(null);
          }
        }}
        eyebrow={def.name}
        title="Mission Specs"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <button
              className="btn btn--ghost btn--sm"
              disabled={saveSpecs.isPending}
              onClick={() => {
                setShowSpecs(false);
                setSpecDraft(null);
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn--primary btn--sm"
              disabled={saveSpecs.isPending || specsQuery.isLoading}
              onClick={() => saveSpecs.mutate(boundSpecIds)}
            >
              {saveSpecs.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <p className="t-sm t-muted" style={{ margin: 0 }}>
            Threads of this agent gain spec_* tools (show/add section/claim/…) for each attached
            spec on their next build (after a reset). Specs can be shared by several agents.
          </p>
          {specsQuery.isLoading ? (
            <Spinner />
          ) : allSpecs.length === 0 ? (
            <p className="t-sm t-muted" style={{ margin: 0 }}>
              No mission specs yet — create one from Mission Specs.
            </p>
          ) : (
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              {allSpecs.map((s) => (
                <li key={s.id}>
                  <label
                    className="t-sm"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      cursor: 'pointer',
                      padding: '4px 0',
                    }}
                  >
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={boundSpecIds.includes(s.id)}
                      onChange={() => toggleSpec(s.id)}
                    />
                    <span style={{ flex: 1 }}>{s.name}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          {saveSpecs.isError && (
            <div className="alert alert--danger">
              {String((saveSpecs.error as Error)?.message ?? 'Failed to save mission specs')}
            </div>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={showDeleteStopped}
        onClose={() => {
          if (!removeStopped.isPending) setShowDeleteStopped(false);
        }}
        onConfirm={() => removeStopped.mutate()}
        eyebrow={def.name}
        title="Delete stopped threads"
        message={`Remove ${stoppedCount} stopped/failed thread${stoppedCount === 1 ? '' : 's'} and their events + state signals? Running threads are untouched. This cannot be undone.`}
        confirmLabel={removeStopped.isPending ? 'Deleting…' : 'Delete stopped threads'}
        destructive={true}
        pending={removeStopped.isPending}
      />
    </div>
  );
}
